import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useUI, type HomeTab, type PanelBody } from '../store/ui'
import { useAccounts } from '../store/accounts'
import { useTabs } from '../store/tabs'
import { useActivityState } from '../store/activityState'
import { buildCacheExport } from '../api/cacheExport'
import { countRecentFailures, listAllProfiles, type CachedProfileRow } from '../cache/db'
import { SearchBox } from './SearchBox'
import { BlurAdultToggle } from './BlurAdultToggle'

const GO_TABS: { key: HomeTab; label: string }[] = [
  { key: 'feed',      label: 'feed' },
  { key: 'activity',  label: 'gallery' },
  { key: 'discovery', label: 'discovery' },
  { key: 'profile',   label: 'profile' },
]

const TOOL_TABS: { key: PanelBody; label: string }[] = [
  { key: 'accounts', label: 'accounts' },
  { key: 'activity', label: 'activity' },
]

const ROUTES: { to: string; label: string }[] = [
  { to: '/schedule',    label: 'schedule' },
  { to: '/tabs-search', label: 'search tabs' },
  { to: '/failure-log', label: 'error log' },
  { to: '/settings',    label: 'settings' },
]

const SWATCH = ['#2f6df0', '#e0533d', '#22a06b', '#b455e0', '#e0a13d', '#38bdf8']

const ACTIVE = 'block-active font-semibold'
const IDLE   = 'text-ink-200 hover:bg-white/5'

export function TopPanel() {
  const navigate = useNavigate()
  const loc = useLocation()
  const homeTab            = useUI((s) => s.homeTab)
  const setHomeTab         = useUI((s) => s.setHomeTab)
  const contentCollapsed   = useUI((s) => s.contentCollapsed)
  const toggleContentCollapsed = useUI((s) => s.toggleContentCollapsed)
  const setContentCollapsed= useUI((s) => s.setContentCollapsed)
  const panel              = useUI((s) => s.panel)
  const setPanel           = useUI((s) => s.setPanel)
  const togglePanel        = useUI((s) => s.togglePanel)
  const setAddAccountOpen  = useUI((s) => s.setAddAccountOpen)
  const activityUnseen     = useUI((s) => s.activityUnseen)

  const accounts  = useAccounts((s) => s.accounts)
  const activeId  = useAccounts((s) => s.activeId)
  const setActive = useAccounts((s) => s.setActive)
  const activeAcc = accounts.find((a) => a.id === activeId)

  // ── failures badge ──
  const [failures, setFailures] = useState(0)
  useEffect(() => {
    let alive = true
    const tick = async () => {
      try {
        const n = await countRecentFailures(Date.now() - 5 * 60_000)
        if (alive) setFailures(n)
      } catch { /* ignore */ }
    }
    void tick()
    const id = setInterval(tick, 10_000)
    return () => { alive = false; clearInterval(id) }
  }, [])

  // ── fullscreen ──
  const [isFs, setIsFs] = useState(false)
  useEffect(() => {
    const onChange = () => setIsFs(!!document.fullscreenElement)
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [])

  function goTab(key: HomeTab) {
    // Profile tab: go directly to the user's own profile page (gallery + right panel)
    if (key === 'profile') {
      if (activeAcc?.handle) {
        setPanel('content')
        navigate(`/profile/${encodeURIComponent(activeAcc.handle)}`)
      }
      return
    }
    if (loc.pathname === '/' && panel === 'content' && homeTab === key) {
      toggleContentCollapsed()
      return
    }
    setContentCollapsed(false)
    setHomeTab(key)
    setPanel('content')
    if (loc.pathname !== '/') navigate('/')
  }

  const onHome = loc.pathname === '/'

  return (
    <div className="surface-bar relative z-30 shrink-0">
      <div className="flex h-11 items-stretch">

        {/* logo */}
        <button
          type="button"
          onClick={() => togglePanel('accounts')}
          className="flex shrink-0 items-center gap-2 px-3 text-xs font-bold tracking-tight text-white"
          style={{ background: panel === 'accounts' ? '#16294d' : '#102341' }}
          title="accounts · settings"
        >
          <span className="text-signal text-sm">◈</span>
          BlueThunder
          <span className="text-[10px] text-ink-400">{panel === 'accounts' ? '▾' : '▸'}</span>
        </button>

        {/* search */}
        <SearchBox />

        {/* go tabs */}
        <div className="no-scrollbar flex min-w-0 items-stretch overflow-x-auto" style={{ background: '#0c1730' }}>
          {GO_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => goTab(t.key)}
              className={`flex items-center whitespace-nowrap px-2.5 text-[12px] ${
                onHome && panel === 'content' && homeTab === t.key ? ACTIVE : IDLE
              }`}
            >
              {t.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => navigate('/posting')}
            className={`flex items-center whitespace-nowrap px-2.5 text-[12px] ${
              loc.pathname === '/posting' ? ACTIVE : IDLE
            }`}
            title="posting pane — compose · pending queue"
          >
            post
          </button>
          <button
            type="button"
            onClick={() => navigate('/schedule')}
            className={`flex items-center whitespace-nowrap px-2.5 text-[12px] ${
              loc.pathname === '/schedule' ? ACTIVE : IDLE
            }`}
            title="scheduled actions — agenda list + month calendar"
          >
            schedule
          </button>
        </div>

        {/* tool tabs */}
        <div className="flex shrink-0 items-stretch">
          {TOOL_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              onClick={() => togglePanel(t.key)}
              className={`relative flex items-center whitespace-nowrap px-2 text-[11px] ${
                panel === t.key ? ACTIVE : IDLE
              }`}
            >
              {t.label}
              {t.key === 'activity' && activityUnseen > 0 && (
                <span className="ml-1 bg-rose-500 px-1 text-[8px] font-bold text-white">
                  {activityUnseen > 9 ? '9+' : activityUnseen}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* failure badge */}
        {failures > 0 && (
          <Link
            to="/failure-log"
            className="flex shrink-0 items-center gap-1 whitespace-nowrap px-2 text-[11px] font-bold text-rose-100 hover:bg-rose-500/15"
          >
            ⚠ {failures}
          </Link>
        )}

        {/* account swatches */}
        <div className="flex shrink-0 items-center gap-1.5 border-l border-white/5 px-2">
          {accounts.map((a, i) => (
            <button
              key={a.id}
              type="button"
              onClick={() => setActive(a.id)}
              title={`@${a.handle}`}
              className="grid h-[20px] w-[20px] place-items-center overflow-hidden text-[9px] font-bold text-white"
              style={{
                background: SWATCH[i % SWATCH.length],
                outline: a.id === activeId ? '2px solid #cbd5e1' : 'none',
                opacity: a.id === activeId ? 1 : 0.55,
              }}
            >
              {a.avatar ? (
                <img src={a.avatar} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer"
                  onError={(e) => { e.currentTarget.style.display = 'none' }} />
              ) : a.handle.slice(0, 1).toUpperCase()}
            </button>
          ))}
          {activeAcc ? (
            <span className="ml-1 hidden text-[11px] font-semibold text-white md:inline">
              @{activeAcc.handle}
            </span>
          ) : (
            <button type="button" onClick={() => setAddAccountOpen(true)}
              className="ml-1 text-[11px] text-ink-200 hover:text-white">
              ＋ add account
            </button>
          )}
        </div>

        {/* gear */}
        <button
          type="button"
          onClick={() => togglePanel('settings')}
          className={`grid w-9 shrink-0 place-items-center text-base ${
            panel === 'settings' ? ACTIVE : 'text-ink-200 hover:bg-white/5'
          }`}
          title="settings"
        >
          ⚙
        </button>

        {/* fullscreen */}
        <button
          type="button"
          onClick={() => isFs ? void document.exitFullscreen() : void document.documentElement.requestFullscreen?.()}
          className="grid w-9 shrink-0 place-items-center text-ink-200 hover:bg-white/5"
          title="fullscreen"
        >
          {isFs ? '⤡' : '⤢'}
        </button>
      </div>
    </div>
  )
}

// ─────────── AccountsBody ───────────

export function AccountsBody() {
  const navigate        = useNavigate()
  const accounts        = useAccounts((s) => s.accounts)
  const activeId        = useAccounts((s) => s.activeId)
  const setActive       = useAccounts((s) => s.setActive)
  const setAddAccountOpen = useUI((s) => s.setAddAccountOpen)
  const setPanel        = useUI((s) => s.setPanel)

  return (
    <div className="flex flex-col gap-2 border-t border-white/5 bg-[#080e1e] p-3">
      <div className="text-[11px] font-bold text-white">Accounts</div>

      <OpsSection title="your accounts" hint={`${accounts.length} signed in`} defaultOpen>
        <div className="flex flex-col gap-2 bg-[#070d1c] p-2">
          <div className="flex flex-wrap gap-1">
            {accounts.length === 0 ? (
              <span className="text-xs text-ink-400">none — add one →</span>
            ) : accounts.map((a, i) => (
              <button
                key={a.id}
                type="button"
                onClick={() => setActive(a.id)}
                className={`flex items-center gap-1.5 border px-2 py-0.5 text-[11px] ${
                  a.id === activeId
                    ? 'border-[#41526e] bg-[#41526e] text-white'
                    : 'border-white/10 bg-white/5 text-ink-200 hover:bg-white/10'
                }`}
              >
                <span className="h-3 w-3 overflow-hidden" style={{ background: SWATCH[i % SWATCH.length] }}>
                  {a.avatar && <img src={a.avatar} alt="" className="h-full w-full object-cover" referrerPolicy="no-referrer" />}
                </span>
                @{a.handle}
                {a.id === activeId && <span className="text-sky-300">◉</span>}
              </button>
            ))}
          </div>
          <button type="button" onClick={() => setAddAccountOpen(true)}
            className="self-start text-[11px] text-ink-200 hover:text-white">
            ＋ add account
          </button>
        </div>
      </OpsSection>

      <OpsSection title="cached accounts" defaultOpen>
        <div className="bg-[#070d1c] p-2">
          <CachedAccountsList navigate={navigate} />
        </div>
      </OpsSection>

      <OpsSection title="display">
        <BlurAdultToggle />
        <OpsRow label="open settings ⚙" hint="schedule · data · all settings"
          onClick={() => setPanel('settings')} trailing="→" />
      </OpsSection>
    </div>
  )
}

function CachedAccountsList({ navigate }: { navigate: (to: string) => void }) {
  const [rows, setRows] = useState<CachedProfileRow[]>([])
  const [filter, setFilter] = useState('')
  useEffect(() => {
    let alive = true
    void listAllProfiles().then((rows) => { if (alive) setRows(rows) })
    return () => { alive = false }
  }, [])

  const q = filter.trim().toLowerCase()
  const visible = q
    ? rows.filter((r) => {
        const p = r.profile as { displayName?: string } | null
        return (
          r.handle.toLowerCase().includes(q) ||
          (p?.displayName ?? '').toLowerCase().includes(q)
        )
      })
    : rows

  return (
    <div className="flex flex-col gap-1">
      <input
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="search cached accounts…"
        className="w-full bg-[#0a1426] px-2 py-1 text-xs text-white outline-none placeholder:text-ink-500 focus:bg-[#0c1730]"
      />
      <div className="flex max-h-64 flex-col gap-px overflow-y-auto">
        {rows.length === 0 ? (
          <div className="px-2 py-1 text-xs text-ink-500">none cached yet.</div>
        ) : visible.length === 0 ? (
          <div className="px-2 py-1 text-xs text-ink-500">no cached account matches “{filter.trim()}”.</div>
        ) : visible.map((r) => {
          const p = r.profile as { displayName?: string; avatar?: string } | null
          return (
            <button
              key={r.did}
              type="button"
              onClick={() => navigate(`/profile/${encodeURIComponent(r.handle)}`)}
              className="flex items-center gap-2 bg-[#070d1c] px-2 py-1 text-left text-xs hover:bg-white/5"
            >
              {p?.avatar ? (
                <img src={p.avatar} alt="" className="h-5 w-5 shrink-0 object-cover" referrerPolicy="no-referrer" loading="lazy" />
              ) : (
                <span className="h-5 w-5 shrink-0 bg-gradient-to-br from-sky-500/30 to-violet-500/30" />
              )}
              <span className="min-w-0 flex-1 truncate text-white">{p?.displayName ?? '@' + r.handle}</span>
              <span className="shrink-0 text-[10px] text-ink-500">@{r.handle}</span>
            </button>
          )
        })}
      </div>
      {rows.length > 0 ? (
        <div className="px-1 text-[9px] text-ink-500">
          {q ? `${visible.length} of ${rows.length}` : `${rows.length}`} cached account{rows.length === 1 ? '' : 's'}
        </div>
      ) : null}
    </div>
  )
}

// ─────────── SettingsBody ───────────

export function SettingsBody() {
  const tabs          = useTabs((s) => s.tabs)
  const excludedTabs  = useActivityState((s) => s.excludedTabs)
  const isIncluded    = (h: string) => !excludedTabs.includes(h.toLowerCase())
  const selectedTabs  = tabs.filter((t) => isIncluded(t.handle))
  const [exporting, setExporting] = useState(false)

  async function exportCache() {
    if (selectedTabs.length === 0 || exporting) return
    setExporting(true)
    try {
      const data = await buildCacheExport(selectedTabs.map((t) => ({ handle: t.handle })), 30)
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url  = URL.createObjectURL(blob)
      const ts   = new Date().toISOString().slice(0, 10).replace(/-/g, '')
      const a    = document.createElement('a')
      a.href = url; a.download = `bskyhq-cache-30d-${ts}.json`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(url)
    } finally { setExporting(false) }
  }

  return (
    <div className="flex flex-col gap-2 border-t border-white/5 bg-[#080e1e] p-3">
      <div className="text-[11px] font-bold text-white">Settings</div>

      <OpsSection title="display" defaultOpen>
        <BlurAdultToggle />
      </OpsSection>

      <OpsSection title="posting" defaultOpen>
        <OpsRow to="/posting" label="posting pane" hint="compose · pending queue" />
        <OpsRow to="/schedule" label="queue" hint="pending & sent actions · month calendar" />
      </OpsSection>

      <OpsSection title="automation">
        <OpsRow to="/auto-follow" label="auto-follow" hint="recurring follow campaigns" />
        <OpsRow to="/ai-campaigns" label="AI campaigns" hint="vision-AI curation rules" />
      </OpsSection>

      <OpsSection title="data & logs">
        <OpsRow to="/tabs-search" label="search tabs" hint="query cached accounts" />
        <OpsRow
          label={exporting ? 'packing…' : `export cache JSON${tabs.length ? ` (${selectedTabs.length}/${tabs.length})` : ''}`}
          hint={selectedTabs.length === 0 ? 'add a watched account first' : 'last 30 days'}
          onClick={() => void exportCache()}
          disabled={selectedTabs.length === 0 || exporting}
          trailing="📦"
        />
        <OpsRow to="/failure-log" label="error log" hint="sync · image · api · scheduler errors" />
      </OpsSection>

      <OpsSection title="account">
        <OpsRow to="/settings" label="all settings" hint="cache · export · DMs · defaults · AI agents" />
        <OpsRow label="＋ add account" hint="sign in another handle"
          onClick={() => useUI.getState().setAddAccountOpen(true)} trailing="" />
      </OpsSection>
    </div>
  )
}

export function OpsSection({
  title, hint, defaultOpen, children,
}: {
  title: string; hint?: string; defaultOpen?: boolean; children: React.ReactNode
}) {
  const [open, setOpen] = useState(!!defaultOpen)
  return (
    <details
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
      className="group border border-white/10"
    >
      <summary
        className="flex cursor-pointer list-none items-center gap-2 bg-[#102036] px-3 py-1.5 hover:bg-white/5"
        style={{ boxShadow: 'inset 2px 0 0 #41526e' }}
      >
        <span className="text-[10px] text-ink-400 transition-transform group-open:rotate-90">›</span>
        <span className="text-[11px] font-bold uppercase tracking-wide text-ink-100">{title}</span>
        {hint && <span className="ml-auto text-[9px] text-ink-500">{hint}</span>}
      </summary>
      {open && <div className="flex flex-col gap-px bg-white/10">{children}</div>}
    </details>
  )
}

export function OpsRow({
  to, label, hint, onClick, disabled, trailing = '→',
}: {
  to?: string; label: string; hint?: string
  onClick?: () => void; disabled?: boolean; trailing?: string
}) {
  const inner = (
    <>
      <div className="min-w-0 flex-1">
        <div className="text-xs text-ink-100">{label}</div>
        {hint && <div className="text-[9px] text-ink-400">{hint}</div>}
      </div>
      <span className="text-ink-300">{trailing}</span>
    </>
  )
  const cls = 'flex items-center gap-2 bg-[#070d1c] px-3 py-2 text-left hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40'
  if (to) return <Link to={to} className={cls}>{inner}</Link>
  return <button type="button" onClick={onClick} disabled={disabled} className={cls}>{inner}</button>
}
